import { useEffect } from 'react';
import { ArrowRight, CalendarDays, CheckCircle2, ClipboardList, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { easeStripe } from '../../../lib/motion';
import { useDiagnosticStore } from '../../../stores/diagnostic';

const REDIRECT_DELAY_MS = 1500;

export function Step5Done() {
  const leadId = useDiagnosticStore((s) => s.leadId);

  // Redirige vers `/diagnostic/resultat?id={leadId}` après une courte
  // animation de confirmation. La page résultat (créée en C1) gère
  // l'affichage enrichi (récap fiches CEE, prochaines étapes, CTA).
  useEffect(() => {
    if (!leadId || typeof window === 'undefined') return;
    const t = window.setTimeout(() => {
      window.location.href = `/diagnostic/resultat?id=${encodeURIComponent(leadId)}`;
    }, REDIRECT_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [leadId]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, ease: easeStripe }}
      className="py-4 text-center"
    >
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-50 ring-8 ring-brand-50/60">
        <CheckCircle2 className="h-8 w-8 text-brand-600" strokeWidth={1.5} />
      </div>
      <h3 className="mt-5 h-section">
        Votre plan est en préparation&nbsp;!
      </h3>
      <p className="mt-2 text-[14px] leading-relaxed text-stone-600">
        Notre équipe analyse votre dossier et vous recontacte sous{' '}
        <strong className="text-ink">24&nbsp;h ouvrées</strong> avec le détail complet.
      </p>

      <div className="mt-6 grid gap-3 text-left sm:grid-cols-2">
        {[
          {
            icon: <ClipboardList className="h-4 w-4 text-brand-600" strokeWidth={1.5} />,
            title: 'Vérification des pièces',
            desc: 'Nous listons les justificatifs exacts pour chaque fiche CEE retenue.',
          },
          {
            icon: <CalendarDays className="h-4 w-4 text-brand-600" strokeWidth={1.5} />,
            title: "Plan d'actions",
            desc: 'Calendrier des travaux, dépôt des dossiers d’aides et calendrier de versement (variable selon le dispositif).',
          },
        ].map((item) => (
          <div key={item.title} className="rounded-xl border border-stone-200 bg-stone-50 p-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-50">
              {item.icon}
            </div>
            <p className="mt-2 text-[13px] font-semibold text-ink">{item.title}</p>
            <p className="mt-0.5 text-[12px] text-stone-500">{item.desc}</p>
          </div>
        ))}
      </div>

      {leadId && (
        <p
          className="mt-6 inline-flex items-center gap-1.5 text-[12px] text-stone-400"
          aria-live="polite"
        >
          <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
          Redirection vers votre tableau de bord…
        </p>
      )}

      <div className="mt-5 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
        <a
          href="/cas-clients"
          className="inline-flex items-center gap-1.5 rounded-lg bg-ink px-4 py-2 text-[13px] font-semibold text-ivory-50 transition-colors hover:bg-stone-800"
        >
          Voir des cas clients similaires
          <ArrowRight className="h-3.5 w-3.5" />
        </a>
        <a
          href="/"
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-stone-600 underline-offset-2 hover:text-ink hover:underline"
        >
          Retour à l'accueil
        </a>
      </div>
    </motion.div>
  );
}
